import { useNavigate, useSearchParams } from "react-router-dom"
import { Box, Grid, Stack, Typography } from "@mui/material"
import { cyan } from "@mui/material/colors"
import { makeStyles } from "tss-react/mui"
import { useSnackbar } from "notistack"
import { Form, Formik, FormikHelpers } from "formik"
import * as Yup from "yup"
import { ROOT_ROUTE } from "../../../constants/routes"
import { usePublicApiMutation } from "../../../core/api/useApiMutation"
import { FormikPasswordField, FormikSubmitButton, FormikTextField } from "../../../shared/components/forms/Formik"
import { UserType, isAuthenticationComplete } from "../loginTypes"
import { activateAccount, getValidatePwRequest, activateUserAccount, validatePassword } from "../loginApi"
import { useAppLocalization } from "../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../localization/components/AppLocalized"
import { useState } from "react"
import { useAppDispatch } from "../../store/storeHooks"
import { login } from "../loginSlice"
import { createEnumChecker } from "../../../lib/enums"
import { useDebouncedCallback } from "use-debounce"
import { AppError } from "../../../core/api/errorTypes"
import { safeParseInt } from "../../../lib/lang"

type FormValues = Readonly<{
    username: string
    password: string
    repeatPassword: string
}>

const isUserType = createEnumChecker(UserType)

const useStyles = makeStyles()((theme) => ({
    form: {
        rowGap: "var(--space-xs)",
    },
    ruleBox: {
        width: "100%",
        color: theme.palette.common.white,
        backgroundColor: cyan[700],
        padding: "var(--space-2xs) var(--space-m)",
        "& > ul": {
            listStyleType: "circle",
            margin: 0,
            padding: 0,
        },
    },
    submitButton: {
        minWidth: "15rem",
        [theme.breakpoints.down("sm")]: {
            width: "100%",
        },
    },
}))

function useInvitationParams() {
    const [searchParams] = useSearchParams()

    const userTypeRaw = searchParams.get("userType")
    const userType = isUserType(userTypeRaw) ? userTypeRaw : undefined

    const companyIdRaw = searchParams.get("companyId")
    const companyId = safeParseInt(companyIdRaw)

    const serviceCenterIdRaw = searchParams.get("serviceCenterId")
    const serviceCenterId = safeParseInt(serviceCenterIdRaw)

    const userGroupIdRaw = searchParams.get("userGroupId")
    const userGroupId = safeParseInt(userGroupIdRaw)

    const oneTimePassword = searchParams.get("otp")
    const defaultUsername = searchParams.get("username")

    let errorMessage = undefined

    if (!userType) {
        errorMessage = `Invitation link was missing valid user type: ${userTypeRaw}`
        throw new AppError("E_INVALID_LINK", "Invalid invitation link", errorMessage)
    }
    if (!companyId) {
        errorMessage = `Invitation link was missing information: companyId`
        throw new AppError("E_INVALID_LINK", "Invalid invitation link", errorMessage)
    }
    if (userType === UserType.OFFICE_MANAGER && !userGroupId) {
        errorMessage = `Invitation link was missing information: userGroupId`
        throw new AppError("E_INVALID_LINK", "Invalid invitation link", errorMessage)
    }
    if (!oneTimePassword) {
        errorMessage = `Invitation link was missing information: otp`
        throw new AppError("E_INVALID_LINK", "Invalid invitation link", errorMessage)
    }

    return {
        userType,
        companyId,
        serviceCenterId,
        userGroupId,
        oneTimePassword,
        defaultUsername,
    }
}

type InvitationProps = Readonly<{}>

export function Invitation({}: InvitationProps) {
    const { classes } = useStyles()

    const [pwErrors, setPwErrors] = useState<string[]>([])

    const navigate = useNavigate()
    const { enqueueSnackbar } = useSnackbar()

    const { l10n } = useAppLocalization()
    const dispatch = useAppDispatch()

    const { mutateAsync: callValidatePasswordRaw } = usePublicApiMutation(validatePassword, {})
    const { mutateAsync: callActivateAccount } = usePublicApiMutation(activateAccount, {})
    const { mutateAsync: callActivateUserAccount } = usePublicApiMutation(activateUserAccount, {})

    const callValidatePassword = useDebouncedCallback(callValidatePasswordRaw, 200)

    const { userType, companyId, serviceCenterId, userGroupId, oneTimePassword, defaultUsername } =
        useInvitationParams()

    const isUserType = userType === UserType.USER

    const validationSchema = Yup.object().shape({
        username: isUserType
            ? Yup.string()
            : Yup.string()
                  .required(l10n.getString("invitation-field-username-required"))
                  .min(3, l10n.getString("invitation-field-username-min-chars", { minChars: 3 })),
        password: Yup.string()
            .required(l10n.getString("invitation-field-password-required"))
            .test({
                name: "is-valid",
                test: async (value, _context) => {
                    try {
                        const request = getValidatePwRequest({
                            userType,
                            companyId,
                            serviceCenterId,
                            userGroupId,
                            password: value,
                        })
                        const response = await callValidatePasswordRaw(request)

                        if (response && !response.validator.success) {
                            setPwErrors(response.validator.rules)
                            return false
                        }

                        setPwErrors([])
                        return true
                    } catch {
                        setPwErrors([])
                        return true
                    }
                },
                message: l10n.getString("invitation-field-password-invalid-for-policy"),
            }),
        repeatPassword: Yup.string()
            .required(l10n.getString("invitation-field-repeatPassword-required"))
            .oneOf([Yup.ref("password")], l10n.getString("invitation-field-repeatPassword-match-password")),
    })

    async function handleSubmit({ username, password }: FormValues, _helpers: FormikHelpers<FormValues>) {
        if (!oneTimePassword) return

        let response

        if (isUserType) {
            response = await callActivateUserAccount({
                oneTimeToken: oneTimePassword,
                password,
            })
        } else {
            response = await callActivateAccount({
                username,
                password,
                authentication: {
                    type: "ONE_TIME_TOKEN",
                    token: oneTimePassword,
                },
            })
        }

        if (isAuthenticationComplete(response)) {
            dispatch(
                login({
                    authToken: response.authentication.token,
                    user: response.user,
                }),
            )

            navigate(ROOT_ROUTE)
        } else {
            enqueueSnackbar({
                variant: "error",
                message: `Client expected authentication complete but server said: ${response.authenticationResult.action}`,
            })
        }
    }

    return (
        <Grid container direction="column" wrap="nowrap" justifyContent="center">
            <Stack gap="1rem" mb="2rem">
                <Grid item>
                    <LocalizedStrict id="invitation-title">
                        <Typography variant="h4" fontWeight={600}>
                            Create your passwords to Facilitynet
                        </Typography>
                    </LocalizedStrict>
                </Grid>
            </Stack>
            <Grid item>
                <Formik<FormValues>
                    initialValues={{ username: defaultUsername ?? "", password: "", repeatPassword: "" }}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    <Form>
                        <Grid
                            container
                            direction="column"
                            wrap="nowrap"
                            alignItems="flex-start"
                            className={classes.form}
                        >
                            {isUserType ? (
                                <Typography>
                                    <LocalizedStrict id="id-card-signup-using-user-name">
                                        <>User name:</>
                                    </LocalizedStrict>{" "}
                                    <Typography component="span" fontWeight="bold">
                                        {defaultUsername}
                                    </Typography>
                                </Typography>
                            ) : (
                                <LocalizedStrict id="invitation-field-username" attrs={{ label: true }}>
                                    <FormikTextField<FormValues>
                                        name="username"
                                        label="Username"
                                        autoComplete="username"
                                        fullWidth
                                    />
                                </LocalizedStrict>
                            )}
                            <LocalizedStrict id="invitation-field-password" attrs={{ label: true }}>
                                <FormikPasswordField<FormValues> name="password" label="Password" fullWidth />
                            </LocalizedStrict>
                            <LocalizedStrict id="invitation-field-repeat-password" attrs={{ label: true }}>
                                <FormikPasswordField<FormValues>
                                    name="repeatPassword"
                                    label="Repeat password"
                                    fullWidth
                                />
                            </LocalizedStrict>
                            {pwErrors.length > 0 && (
                                <Box className={classes.ruleBox}>
                                    <ul>
                                        {pwErrors.map((message) => (
                                            <li key={message}>{message}</li>
                                        ))}
                                    </ul>
                                </Box>
                            )}
                            <LocalizedStrict id="invitation-submit-button" attrs={{ loadingLabel: true }}>
                                <FormikSubmitButton fullWidth={false} className={classes.submitButton}>
                                    Opret
                                </FormikSubmitButton>
                            </LocalizedStrict>
                        </Grid>
                    </Form>
                </Formik>
            </Grid>
        </Grid>
    )
}
